import { useCallback, useState, type ReactNode } from "react";
import { Link } from "react-router";
import { VimeoFacade } from "@/components/VimeoFacade";
import { voiceInterviews } from "@/data/voices";
import { VoiceWall } from "@/components/VoiceWall";
import { useAnalytics } from "@/hooks/useAnalytics";
import { logoNodes } from "@/components/TechChip";
import { SectionHead } from "@/pages/index/parts";
import { NavIcon } from "@/components/NavIcon";
import { InterviewIcon, ReviewIcon, type PdIcon } from "@/pages/lp/prod-dev/icons";
import { VOICE_LABEL } from "@/data/voice-labels";

/**
 * 受講生の声。長く読ませず、数で見せる。
 * 頭に実数の札 (インタビュー何本、回答何件、載せた声何件)、続けて声の札を4列 (スマホは2列) で並べ、
 * 札を押すと全文が開く。そのあとにインタビュー映像のサムネイルを横一列に置く。地は青みの白。
 *
 * 数字は app/data/voices.ts の voiceInterviews と、この中の VOICES を数えた値だけ。
 * 数え方は NUMS の上に書いた。作った数も丸めた数も置かない。
 *
 * 発言は voices.ts の実インタビューが正。太字にしているのは元データで == == が付いている箇所で、
 * こちらで強調位置を足していない。札の一言 (head) は、その発言の中の字をそのまま切り出したもの。
 * 札の絵は、映像がこの節に出ている話し手はその映像のサムネイル、出ていない話し手は属性を表す図記号。
 * 図記号は icons.tsx と同じ作り (24 の格子、墨の線 2、右下へずらした青い面)。人の写真は使わない。
 *
 * 映像は押したときだけ再生する (サムネイルは VimeoFacade が出す)。押した映像は列の先頭で横幅いっぱいに広がる。
 * 計測は onIframeReady の index (VIDEOS の並び順) を親が見ている。順番は変えない。
 *
 * 声の札と映像は、PC でもスマホでも1行の横スクロール。右端を見切れさせて「まだある」と見せ、
 * 矢印のボタンでも送れる。動きは CSS の scroll-snap と scrollBy だけで、state は持たない。
 * 矢印の処理は scrollRow。書き出し (apps/women-astro) の素の JS (hp.ts の rows) も同じ式で動かす。
 * 声の札と映像のあいだに、受講生の X と note の投稿を横に流す壁 (VoiceWall) を置く。載せる投稿が無いあいだは出ない。
 * 足し方は data/voice-posts.ts と data/voice-notes.ts の頭に書いた。
 */

type Video = { id: string; youtubeId?: string; title: string; note?: { url: string; label: string } };

const VIDEOS: Video[] = [
  {
    id: "1164598984",
    title: "なべちゃんさん 30代 技術開発職 2児の母 未経験",
    note: { url: "https://note.com/clever_cobra2626/n/ndce87b7f03e9", label: "学習記録を読む（note）" },
  },
  { id: "1163288065", title: "あさみさん 27歳 IT未経験" },
  { id: "1155686191", title: "宮崎さん 27歳 フリーター 未経験" },
  { id: "1159471067", title: "ユウキさん 25歳 IT企業勤務" },
  { id: "1155472986", title: "あいねさん 23歳 私立教員 未経験" },
  { id: "1185117557", title: "フリーランスデザイナー 主婦" },
  /* ここから /lab/voices の受講生インタビュー (YouTube)。出どころは各記事のいちばん上の動画 */
  { id: "yt-fForlmlrvn8", youtubeId: "fForlmlrvn8", title: "ゆりさん 33歳 IT企業営業職" },
  { id: "yt-Exuywjm6UBE", youtubeId: "Exuywjm6UBE", title: "ポテトさん 30代 休職中" },
  { id: "yt-7OfDADfK2t0", youtubeId: "7OfDADfK2t0", title: "ユウキさん 30代前半 メーカー企画職" },
];

/** タブレットは2列、PC は3列の格子 (CSS の hx-vfilms)。9本なら3段でそろう。スマホは横に流す */
const FILM_COLS_CLASS = "hx-vfilms";

/** 矢印を押したとき、同じ囲み (data-hx-rowbox) の中の横の列 (data-hx-row) を送る。動きを減らす設定なら即座に */
export function scrollRow(from: HTMLElement, dir: 1 | -1) {
  const row = from.closest("[data-hx-rowbox]")?.querySelector<HTMLElement>("[data-hx-row]");
  if (!row) return;
  const reduce = typeof window !== "undefined" && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  row.scrollBy({ left: dir * row.clientWidth * 0.8, behavior: reduce ? "auto" : "smooth" });
}

function RowArrows({ label, className = "" }: { label: string; className?: string }) {
  return (
    <span className={`hx-arrs ${className}`.trim()}>
      {([-1, 1] as const).map((dir) => (
        <button
          key={dir}
          type="button"
          className="hx-arr"
          aria-label={`${label}を${dir < 0 ? "前へ" : "次へ"}`}
          data-hx-arrow={dir}
          onClick={(e) => scrollRow(e.currentTarget, dir)}
        >
          <svg viewBox="0 0 16 16" width="16" height="16" aria-hidden="true" focusable="false" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d={dir < 0 ? "M10 3L5 8l5 5" : "M6 3l5 5-5 5"} />
          </svg>
        </button>
      ))}
    </span>
  );
}

/** 札の絵。thumb はこの節に映像が出ている話し手のサムネイル、glyph は属性を表す図記号 */
type Art = { kind: "thumb"; videoId: string } | { kind: "glyph"; name: "teacher" | "homework" };

type Voice = {
  /** 設問から起こしたラベル。開いたときの全文の頭に出す */
  topic: string;
  /** 太字にする箇所を ** で挟む。位置は元データの == == と同じ */
  quote: string;
  /** 札に大きく出す一言。quote の中の字をそのまま切り出す (** を除いた字の中に必ずある) */
  head: string;
  speaker: string;
  art: Art;
};

export const VOICES: Voice[] = [
  {
    topic: "講師とサポート",
    quote: "**講師の方々がレスポンスが早くて**、分からないことを質問するとすぐに回答が返ってくるので、とってもいいです。",
    head: "講師の方々がレスポンスが早くて",
    speaker: VOICE_LABEL.asami,
    art: { kind: "thumb", videoId: "1163288065" },
  },
  {
    topic: "選んだ理由",
    quote: "ゆめさくエンジニアさんで一番いいなと思ったのは、**すぐに質問をチャットで答えてくれる点**と、**1ヶ月ごとにワンオンワンで悩みや今後の道のりを一緒に考えてくれる**っていうところをすごく魅力に感じました。",
    head: "すぐに質問をチャットで答えてくれる点",
    speaker: VOICE_LABEL.aine,
    art: { kind: "glyph", name: "teacher" },
  },
  {
    topic: "学習ペース",
    quote: "**18ヶ月かけてじっくり学べる**ところと、**自分のペースで進められる**ところが自分に合っていました。",
    head: "18ヶ月かけてじっくり学べる",
    speaker: VOICE_LABEL.asami,
    art: { kind: "thumb", videoId: "1163288065" },
  },
  {
    topic: "教える内容",
    quote: "**「ただコードが書けるだけじゃ意味がないと思ってる」**という話を聞いて、**自分の想像してる「できる」のレベルに近いのがここなのかな**と思いました。",
    head: "ただコードが書けるだけじゃ意味がない",
    speaker: VOICE_LABEL.yuukiIt,
    art: { kind: "thumb", videoId: "1159471067" },
  },
  {
    topic: "続けやすさ",
    quote: "受講生が集まる部屋みたいなのがあって、そこで何でも質問ができたりとか、自己紹介をし合ったりとか。そこで**一緒に頑張っている仲間がいるんだな**っていうのがすごく心強いです。",
    head: "一緒に頑張っている仲間がいる",
    speaker: VOICE_LABEL.aine,
    art: { kind: "glyph", name: "teacher" },
  },
  {
    topic: "入会後の変化",
    quote: "**分からないことを分からないままにしない、恐れなくなった**っていうのが一番の変化ですかね。",
    head: "分からないことを分からないままにしない",
    speaker: VOICE_LABEL.asami,
    art: { kind: "thumb", videoId: "1163288065" },
  },
  {
    topic: "サポートの手厚さ",
    quote: "**Zoomでの講義、チャットコミュニティ、そしてメンターさんとの月1回の面談など、本当にサポートが手厚い**です。",
    head: "本当にサポートが手厚い",
    speaker: "受講生（ママ・フリーランス）",
    art: { kind: "glyph", name: "homework" },
  },
  {
    topic: "選んだ理由",
    quote: "**期間が長い**っていうのと、**ロードマップがしっかりしていて体系的に広い範囲で学べる**っていうのが大きいなと思ってます。",
    head: "体系的に広い範囲で学べる",
    speaker: VOICE_LABEL.nabe,
    art: { kind: "thumb", videoId: "1164598984" },
  },
];

/*
  実数の札。数え方は次の通り。
  - インタビュー記事 本 = app/data/voices.ts の voiceInterviews の件数 (インタビュー記事と映像が1人ずつ付いている)
  - インタビューの回答 件 = voiceInterviews の全員分の content (設問と回答の組) の件数の合計
  - 載せた受講生の声 件 = この下の VOICES の件数 (札の数と同じ)
*/
const VoicesIcon: PdIcon = (p) => <NavIcon name="voices" size={p.size} className={p.className} />;

const NUMS: { n: number; unit: string; label: string; Icon: PdIcon }[] = [
  { n: voiceInterviews.length, unit: "本", label: "インタビュー記事", Icon: InterviewIcon },
  {
    n: voiceInterviews.reduce((sum, v) => sum + v.content.length, 0),
    unit: "件",
    label: `インタビューの回答 全${voiceInterviews.length}本の合計`,
    Icon: ReviewIcon,
  },
  { n: VOICES.length, unit: "件", label: "ここに載せた受講生の声", Icon: VoicesIcon },
];

/** ** で挟まれた箇所だけ太字にする。元データの == == と同じ位置 */
function Emphasized({ text }: { text: string }) {
  // 受講生の勤め先の LINE にはロゴを付けない
  const seen = new Set<string>(["LINE"]);
  return (
    <>
      {text.split("**").map((part, i) =>
        i % 2 === 1 ? <strong key={i}>{logoNodes(part, seen)}</strong> : <span key={i}>{logoNodes(part, seen)}</span>
      )}
    </>
  );
}

/* 属性の図記号。icons.tsx と同じ作りで、この節だけで要る2つを描いた */
function Glyph({ name }: { name: "teacher" | "homework" }) {
  let body: ReactNode;
  if (name === "teacher") {
    /* 黒板と脚。教員 */
    body = (
      <>
        <g transform="translate(1.8 1.5)" fill="var(--blue)" stroke="none">
          <rect x="3" y="4" width="18" height="12" rx="1.5" />
        </g>
        <rect x="3" y="4" width="18" height="12" rx="1.5" fill="#fff" />
        <path d="M7 9h7M7 12h10M8 20l1.5-4M16 20l-1.5-4" />
      </>
    );
  } else {
    /* 家のなかのノートパソコン。在宅で働く */
    body = (
      <>
        <g transform="translate(1.8 1.5)" fill="var(--blue)" stroke="none">
          <path d="M5 10.5l7-6 7 6V20H5z" />
        </g>
        <path d="M5 10.5l7-6 7 6V20H5z" fill="#fff" />
        <rect x="9" y="12" width="6" height="4" rx="0.8" />
        <path d="M8 18.5h8" />
      </>
    );
  }
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      {body}
    </svg>
  );
}

function VoiceCard({ voice }: { voice: Voice }) {
  return (
    <li>
      <details className="hx-vc" name="hx-voice">
        <summary>
          <span className="hx-vc-art">
            {voice.art.kind === "thumb" ? (
              <img
                src={`https://vumbnail.com/${voice.art.videoId}.jpg`}
                alt=""
                width={640}
                height={360}
                loading="lazy"
                decoding="async"
              />
            ) : (
              <Glyph name={voice.art.name} />
            )}
            <i className="hx-vc-pm" aria-hidden="true" />
          </span>
          <span className="hx-vc-h">{voice.head}</span>
          <span className="hx-vc-who">{voice.speaker}</span>
        </summary>
        <div className="hx-vc-full">
          <p className="hx-vc-tp">{voice.topic}</p>
          <blockquote>
            「<Emphasized text={voice.quote} />」
          </blockquote>
        </div>
      </details>
    </li>
  );
}

/** 映像の1枚。押すと再生し、列の先頭で横幅いっぱいに広がる */
function FilmTile({
  video,
  index,
  on,
  onPlay,
  onIframeReady,
}: {
  video: Video;
  index: number;
  on: boolean;
  onPlay: (index: number) => void;
  onIframeReady: (index: number, iframe: HTMLIFrameElement) => void;
}) {
  const ready = useCallback((iframe: HTMLIFrameElement) => onIframeReady(index, iframe), [onIframeReady, index]);
  const activate = useCallback(() => onPlay(index), [onPlay, index]);
  return (
    <li className={on ? "hx-vf hx-vf-on" : "hx-vf"}>
      <div className="hx-vf-box">
        <VimeoFacade
          videoId={video.id}
          youtubeId={video.youtubeId}
          params="h=&title=0&byline=0&portrait=0"
          title={`受講生インタビュー ${video.title}`}
          onActivate={activate}
          onIframeReady={ready}
        />
        {!on && (
          <span className="hx-vf-cap" aria-hidden="true">
            {video.title}
          </span>
        )}
      </div>
      {on && <p className="hx-vf-name">{video.title}</p>}
    </li>
  );
}

export function VoicesShowcase({
  onIframeReady,
}: {
  onIframeReady: (index: number, iframe: HTMLIFrameElement) => void;
}) {
  const [playing, setPlaying] = useState<number | null>(null);
  const { trackCTAClick } = useAnalytics();

  return (
    <section id="voices" className="hx-sec hx-wash hx-voices">
      <div className="hx-wrap">
        <div className="hx-vhead">
          <SectionHead
            label="受講生の声"
            size="s"
            lede={
              <>
                インタビューの発言をそのまま載せています。<b><mark className="hx-hl"><span className="whitespace-nowrap">言い回しは</span>変えていません</mark></b>。
              </>
            }
          >
            <ins className="hx-gr">未経験</ins>から始めた受講生の声
          </SectionHead>

          <ul className="hx-vnums">
            {NUMS.map((s) => (
              <li key={s.label}>
                <span className="hx-vnum">
                  <s.Icon size={24} className="hx-vnum-ic" />
                  {s.n}
                  <small>{s.unit}</small>
                </span>
                <span className="hx-vnum-l">{s.label}</span>
              </li>
            ))}
          </ul>
        </div>

        {/* 声の札。押すと全文が開く。開くのは1枚ずつ */}
        <div data-hx-rowbox>
          <div className="hx-arrbar">
            <RowArrows label="受講生の声" />
          </div>
          <ul className="hx-vcards" data-hx-row>
            {VOICES.map((voice) => (
              <VoiceCard key={voice.quote} voice={voice} />
            ))}
          </ul>
        </div>

        {/* 受講生が X と note に書いた投稿を 2 段で横に流す。載せる投稿 (data/voice-posts.ts、data/voice-notes.ts) が無いあいだは出さない */}
        <VoiceWall />

        {/* インタビュー映像。押したときだけ再生する */}
        <div data-hx-rowbox>
        <div className="hx-vf-hd">
          <p className="hx-vf-t">
            <span className="hx-k">インタビュー</span>
            <span className="hx-vf-name">インタビュー映像 {VIDEOS.length}本</span>
            <span className="hx-vf-sub">（受講生の声のページに{VIDEOS.length - VIDEOS.filter((v) => v.youtubeId).length}本、研究所に{VIDEOS.filter((v) => v.youtubeId).length}本）</span>
          </p>
          <p className="hx-vf-links">
            {VIDEOS.map(
              (v) =>
                v.note && (
                  <a key={v.id} href={v.note.url} target="_blank" rel="noopener noreferrer" className="hx-tlink hx-vf-more">
                    {v.title.split(" ")[0]}の{v.note.label}
                  </a>
                ),
            )}
            <Link
              to="/voices"
              onClick={() => trackCTAClick("voices_more", "voices_section")}
              className="hx-tlink hx-vf-more"
            >
              受講生の声をもっと見る
            </Link>
            <RowArrows label="インタビュー映像" className="hx-arrs-film" />
          </p>
        </div>
        <ul className={FILM_COLS_CLASS} data-hx-row>
          {VIDEOS.map((v, i) => (
            <FilmTile
              key={v.id}
              video={v}
              index={i}
              on={playing === i}
              onPlay={setPlaying}
              onIframeReady={onIframeReady}
            />
          ))}
        </ul>
        </div>
      </div>
    </section>
  );
}
